/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/*
 * Broadcast Theme
 *
 * Use this file to add custom Javascript to Broadcast.  Keeping your custom
 * Javascript in this fill will make it easier to update Broadcast. In order
 * to use this file you will need to open layout/theme.liquid and uncomment
 * the custom.js script import line near the bottom of the file.
 */

(function () {
  // Add custom code below this line

  // Discount Code Functionality - Advanced AJAX Version
  console.log("[DISCOUNT] Advanced script loading...");

  class DiscountCode {
    constructor(container) {
      this.container = container;
      this.input = container.querySelector('[id^="discount-code-input"]');
      this.button = container.querySelector('[id^="apply-discount-button"]');
      this.message = container.querySelector('[id^="discount-message"]');
      this.isDrawer =
        container.closest(".cart__drawer-form") !== null ||
        container.closest("cart-drawer") !== null ||
        container.closest(".drawer") !== null;

      console.log("[DISCOUNT] Script loaded for container:", container);
      console.log("[DISCOUNT] Is drawer:", this.isDrawer);
      this.init();
    }

    init() {
      if (this.button && this.input) {
        console.log("[DISCOUNT] Button and input found");
        this.button.addEventListener("click", this.applyDiscount.bind(this));

        // Allow applying discount with Enter key
        this.input.addEventListener("keypress", (e) => {
          if (e.key === "Enter") {
            this.applyDiscount();
          }
        });
      } else {
        console.warn("[DISCOUNT] Button or input not found");
      }
    }

    formatMoney(cents) {
      // Format money according to Turkish format
      const amount = (cents / 100).toFixed(2);
      const parts = amount.split(".");
      const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return integerPart + "," + parts[1] + " TL";
    }

    updateCartUI(cart) {
      console.log("[DISCOUNT] Updating cart UI...", cart);

      // Update subtotal with discount info (without changing the main price)
      const subtotalElement =
        document.querySelector("[data-subtotal]") ||
        document.querySelector(".totals__subtotal-value") ||
        document.querySelector(".cart__price__subtotal");
      console.log("[DISCOUNT] Subtotal element found:", subtotalElement);

      // Update discounts section - try multiple selectors for drawer vs cart page
      const discountsElement =
        document.querySelector("[data-discounts]") ||
        document.querySelector(".cart__total__discount") ||
        document.querySelector(".discounts") ||
        document.querySelector(".cart__discounts");

      console.log("[DISCOUNT] Discounts element found:", discountsElement);

      if (discountsElement) {
        if (
          cart.cart_level_discount_applications &&
          cart.cart_level_discount_applications.length > 0
        ) {
          const discountsHTML = cart.cart_level_discount_applications
            .map((discount) => {
              const discountAmount = this.formatMoney(
                discount.total_allocated_amount
              );

              // Different HTML structure for drawer vs cart page
              if (this.isDrawer) {
                return `
                <div class="cart__total cart__discount-item">
                  <div class="cart__discount-label">
                    <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-tags" viewBox="0 0 25 26">
                      <g stroke="#000" stroke-width=".875">
                        <path d="M4.304.79h8.571l12 11.835M9.594 4.17H2.589c-.946 0-1.714.758-1.714 1.692v6.907c0 .449.18.88.502 1.196l10.47 10.329c.67.66 1.755.66 2.423 0 .173-.17.308-.376.394-.602l1.64-4.304 4.364-1.615a1.684 1.684 0 0 0 .61-2.779L10.805 4.666a1.725 1.725 0 0 0-1.211-.495Z"></path>
                        <path d="M6.018 8.82a.426.426 0 0 0-.429.423c0 .234.192.423.429.423.236 0 .428-.19.428-.423a.424.424 0 0 0-.426-.422h-.002"></path>
                      </g>
                    </svg>
                    <span>${discount.title}</span>
                  </div>
                  <span class="cart__discount-amount">-${discountAmount}</span>
                </div>
              `;
              } else {
                return `
                <li class="discounts__discount discounts__discount--end">
                  <svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" class="icon icon-discount color-foreground-75">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="m7 1 3 3-6 6-3-3 6-6Z"></path>
                  </svg>
                  ${discount.title} (-${discountAmount})
                </li>
              `;
              }
            })
            .join("");
          discountsElement.innerHTML = discountsHTML;

          // Show the discount section
          discountsElement.classList.remove("hidden");
          if (discountsElement.parentElement) {
            discountsElement.parentElement.classList.remove("hidden");
          }
        } else {
          discountsElement.innerHTML = "";
          discountsElement.classList.add("hidden");
        }
      }

      // Add discount savings info above/below subtotal - more prominent for drawer
      if (
        cart.cart_level_discount_applications &&
        cart.cart_level_discount_applications.length > 0
      ) {
        const totalDiscountAmount =
          cart.cart_level_discount_applications.reduce((sum, discount) => {
            return sum + discount.total_allocated_amount;
          }, 0);

        const savingsText = `Indirimden kazancınız: ${this.formatMoney(
          totalDiscountAmount
        )}`;

        // Remove existing savings info
        const existingSavings = document.querySelectorAll(
          ".discount-savings-info"
        );
        existingSavings.forEach((el) => el.remove());

        // Add new savings info
        const savingsDiv = document.createElement("div");
        savingsDiv.className = this.isDrawer
          ? "discount-savings-info discount-savings-info--drawer"
          : "discount-savings-info discount-savings-info--page";

        savingsDiv.textContent = savingsText;

        // Insert savings info in appropriate location
        if (subtotalElement && subtotalElement.parentNode) {
          if (this.isDrawer) {
            // In drawer, add it prominently after the subtotal section
            const cartPriceHolder = subtotalElement.closest(
              ".cart__price__holder"
            );
            if (cartPriceHolder) {
              cartPriceHolder.appendChild(savingsDiv);
            } else {
              subtotalElement.parentNode.appendChild(savingsDiv);
            }
          } else {
            subtotalElement.parentNode.appendChild(savingsDiv);
          }
        }
      }

      // Update cart count if available
      const cartCountElements = document.querySelectorAll("[data-cart-count]");
      cartCountElements.forEach((el) => {
        el.textContent = cart.item_count;
      });

      // Clear input
      this.input.value = "";
    }

    applyDiscount() {
      const code = this.input.value.trim();
      console.log("[DISCOUNT] Apply clicked with code:", code);

      if (!code) {
        this.message.textContent = "Lütfen bir indirim kodu giriniz.";
        this.message.style.color = "#f05832";
        return;
      }

      this.button.disabled = true;
      this.button.textContent = "Uygulanıyor...";
      this.message.textContent = "İndirim uygulanıyor...";
      this.message.style.color = "#333";

      // First check if discount is already applied
      fetch("/cart.js")
        .then((res) => res.json())
        .then((currentCart) => {
          console.log("[DISCOUNT] Current cart:", currentCart);

          // Check if this exact discount code is already applied
          const existingDiscounts =
            currentCart.cart_level_discount_applications || [];
          const isDuplicateDiscount = existingDiscounts.some((discount) => {
            // More precise matching - check if the discount title exactly matches or ends with the code
            const discountTitle = discount.title.toLowerCase();
            const inputCode = code.toLowerCase();
            return (
              discountTitle === inputCode ||
              discountTitle.endsWith(` ${inputCode}`) ||
              discountTitle.startsWith(`${inputCode} `)
            );
          });

          console.log("[DISCOUNT] Existing discounts:", existingDiscounts);
          console.log("[DISCOUNT] Is duplicate?", isDuplicateDiscount);

          if (isDuplicateDiscount) {
            this.message.textContent = `Discount "${code}" is already applied to your cart.`;
            this.message.style.color = "#f05832";
            this.button.disabled = false;
            this.button.textContent = "Apply";
            return Promise.reject("duplicate"); // Stop the chain
          }

          // Proceed with applying the discount
          return fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ discount: code }),
          });
        })
        .then((updateResponse) => {
          console.log(
            "[DISCOUNT] Update response status:",
            updateResponse.status
          );
          console.log("[DISCOUNT] Update response ok:", updateResponse.ok);
          return updateResponse.json().catch(() => ({})); // Handle empty response
        })
        .then((updateResult) => {
          console.log("[DISCOUNT] Update result:", updateResult);
          // Wait a bit then fetch cart
          return new Promise((resolve) => setTimeout(resolve, 300)).then(() =>
            fetch("/cart.js")
          );
        })
        .then((res) => res.json())
        .then((cart) => {
          console.log("[DISCOUNT] Full cart response:", cart);

          // Check multiple possible discount properties
          const cartLevelDiscounts =
            cart.cart_level_discount_applications || [];
          const totalDiscounts = cart.total_discount || 0;
          const discounts = cart.discounts || [];

          console.log("[DISCOUNT] Cart level discounts:", cartLevelDiscounts);
          console.log("[DISCOUNT] Total discount amount:", totalDiscounts);
          console.log("[DISCOUNT] Discounts array:", discounts);

          // Check if any discount is applied
          const hasCartLevelDiscount = cartLevelDiscounts.length > 0;
          const hasTotalDiscount = totalDiscounts > 0;
          const hasDiscountsArray = discounts.length > 0;

          const applied =
            hasCartLevelDiscount || hasTotalDiscount || hasDiscountsArray;

          if (applied) {
            this.message.textContent = `İndirim kodu "${code}" başarıyla uygulandı!`;
            this.message.style.color = "green";

            // Update UI without reloading
            this.updateCartUI(cart);

            // Only reload cart page, never redirect from drawer
            if (!this.isDrawer) {
              setTimeout(() => {
                location.reload();
              }, 1000);
            }
          } else {
            this.message.textContent = `İndirim kodu "${code}" geçersiz veya uygulanamadı.`;
            this.message.style.color = "red";
          }

          console.log("[DISCOUNT] Applied status:", applied);
        })
        .catch((err) => {
          if (err === "duplicate") {
            // Already handled above, don't show another error
            return;
          }
          this.message.textContent = "İndirim uygulanamadı.";
          this.message.style.color = "red";
          console.error("[DISCOUNT] Error applying discount:", err);
        })
        .finally(() => {
          this.button.disabled = false;
          this.button.textContent = "Apply";
        });
    }
  }

  // Initialize discount code functionality when DOM is loaded
  document.addEventListener("DOMContentLoaded", () => {
    console.log("[DISCOUNT] DOM Content Loaded, initializing...");

    // Initialize for cart page
    const cartPageDiscount = document.querySelector(".cart__discount");
    if (cartPageDiscount && !cartPageDiscount.closest("cart-drawer")) {
      console.log("[DISCOUNT] Cart page discount found");
      new DiscountCode(cartPageDiscount);
    }

    // Initialize for cart drawer
    const drawerDiscount = document.querySelector(
      "cart-drawer .cart__discount"
    );
    if (drawerDiscount) {
      console.log("[DISCOUNT] Drawer discount found");
      new DiscountCode(drawerDiscount);
    }
  });

  // Also initialize when cart drawer is opened (for dynamic content)
  document.addEventListener("cart:drawer:open", () => {
    console.log("[DISCOUNT] Cart drawer opened");
    setTimeout(() => {
      const drawerDiscount = document.querySelector(
        "cart-drawer .cart__discount"
      );
      if (drawerDiscount && !drawerDiscount.hasDiscountCode) {
        drawerDiscount.hasDiscountCode = true;
        console.log("[DISCOUNT] Initializing drawer discount");
        new DiscountCode(drawerDiscount);
      }
    }, 100);
  });

  // Initialize when cart drawer content changes
  if (typeof window.CartDrawer !== "undefined") {
    const originalOpen = window.CartDrawer.open;
    window.CartDrawer.open = function () {
      originalOpen.apply(this, arguments);
      setTimeout(() => {
        const drawerDiscount = document.querySelector(
          "cart-drawer .cart__discount"
        );
        if (drawerDiscount && !drawerDiscount.hasDiscountCode) {
          drawerDiscount.hasDiscountCode = true;
          new DiscountCode(drawerDiscount);
        }
      }, 200);
    };
  }

  // MutationObserver to watch for cart drawer content changes
  const observeCartDrawer = () => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            // Check if discount code elements were added
            const drawerDiscount = cartDrawer.querySelector(".cart__discount");
            if (drawerDiscount && !drawerDiscount.hasDiscountCode) {
              console.log(
                "[DISCOUNT] New discount code element found in drawer"
              );
              drawerDiscount.hasDiscountCode = true;
              new DiscountCode(drawerDiscount);
            }
          }
        });
      });

      observer.observe(cartDrawer, {
        childList: true,
        subtree: true,
      });
    }
  };

  // Initialize observer when DOM is ready
  setTimeout(() => {
    observeCartDrawer();
  }, 1000);

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.

  // =================================================================================================
  // Quick View Popup Functionality
  // =================================================================================================

  class QuickViewPopup {
    constructor() {
      this.popup = null;
      this.overlay = null;
      this.modal = null;
      this.closeBtn = null;
      this.loadingEl = null;
      this.productDetailsEl = null;
      this.isOpen = false;
      this.currentRequest = null;

      this.init();
    }

    init() {
      // Create popup HTML if it doesn't exist
      this.createPopup();

      // Add event listeners
      this.addEventListeners();

      console.log("[QUICK VIEW] Initialized");
    }

    createPopup() {
      // Check if popup already exists
      if (document.getElementById("quick-view-popup")) {
        this.popup = document.getElementById("quick-view-popup");
        this.overlay = this.popup.querySelector(".quick-view-overlay");
        this.modal = this.popup.querySelector(".quick-view-modal");
        this.closeBtn = this.popup.querySelector(".quick-view-close");
        this.loadingEl = this.popup.querySelector(".quick-view-loading");
        this.productDetailsEl = this.popup.querySelector(
          ".quick-view-product-details"
        );
        return;
      }

      // Create popup HTML
      const popupHTML = `
        <div class="quick-view-popup" id="quick-view-popup">
          <div class="quick-view-overlay"></div>
          <div class="quick-view-modal">
            <div class="quick-view-header">
              <h3 class="quick-view-title">Quick View</h3>
              <button class="quick-view-close" aria-label="Close quick view">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div class="quick-view-content">
              <div class="quick-view-loading">
                <div class="loading-spinner"></div>
                <p>Loading product details...</p>
              </div>
              <div class="quick-view-product-details" style="display: none;">
                <!-- Product content will be loaded here -->
              </div>
            </div>
          </div>
        </div>
      `;

      // Add to body
      document.body.insertAdjacentHTML("beforeend", popupHTML);

      // Get references
      this.popup = document.getElementById("quick-view-popup");
      this.overlay = this.popup.querySelector(".quick-view-overlay");
      this.modal = this.popup.querySelector(".quick-view-modal");
      this.closeBtn = this.popup.querySelector(".quick-view-close");
      this.loadingEl = this.popup.querySelector(".quick-view-loading");
      this.productDetailsEl = this.popup.querySelector(
        ".quick-view-product-details"
      );
    }

    addEventListeners() {
      // Quick view button clicks
      document.addEventListener("click", (e) => {
        if (e.target.classList.contains("quick-view-button")) {
          e.preventDefault();
          const productUrl = e.target.dataset.productUrl;
          const productId = e.target.dataset.productId;
          this.open(productUrl, productId);
        }
      });

      // Close button
      this.closeBtn.addEventListener("click", () => {
        this.close();
      });

      // Overlay click
      this.overlay.addEventListener("click", () => {
        this.close();
      });

      // ESC key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.isOpen) {
          this.close();
        }
      });
    }

    async open(productUrl, productId) {
      if (this.isOpen) {
        this.close();
      }

      console.log("[QUICK VIEW] Opening popup for product:", productId);

      // Show popup
      this.popup.classList.add("active");
      this.isOpen = true;

      // Show loading
      this.loadingEl.style.display = "flex";
      this.productDetailsEl.style.display = "none";

      // Cancel previous request if exists
      if (this.currentRequest) {
        this.currentRequest.abort();
      }

      try {
        // Create new AbortController for this request
        this.currentRequest = new AbortController();

        // Fetch product data
        const response = await fetch(productUrl, {
          signal: this.currentRequest.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const html = await response.text();

        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        console.log("[QUICK VIEW] HTML loaded, length:", html.length);
        console.log("[QUICK VIEW] Looking for .section-main-product...");

        // Extract product content
        const productContent = doc.querySelector(".section-main-product");

        if (!productContent) {
          console.log("[QUICK VIEW] .section-main-product not found");
          console.log(
            "[QUICK VIEW] Available sections:",
            doc.querySelectorAll("section")
          );
          console.log(
            "[QUICK VIEW] Available classes with 'product':",
            doc.querySelectorAll("*[class*='product']")
          );
          console.log(
            "[QUICK VIEW] Page title:",
            doc.querySelector("title")?.textContent
          );
          throw new Error("Product content not found");
        }

        console.log(
          "[QUICK VIEW] Found .section-main-product:",
          productContent
        );

        // Clone and modify the content for quick view
        const quickViewContent = await this.prepareQuickViewContent(
          productContent.cloneNode(true),
          productUrl
        );

        // Hide loading and show content
        this.loadingEl.style.display = "none";
        this.productDetailsEl.style.display = "block";
        this.productDetailsEl.innerHTML = "";
        this.productDetailsEl.appendChild(quickViewContent);

        // Initialize any scripts that might be needed
        // Temporarily disabled to prevent theme script conflicts
        // this.initializeQuickViewScripts();

        // Fix variant selection sync issue
        setTimeout(() => {
          try {
            this.fixVariantSync();
          } catch (error) {
            console.log("[QUICK VIEW] Variant sync failed:", error);
          }
        }, 300);

        console.log("[QUICK VIEW] Popup opened successfully");
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("[QUICK VIEW] Request was aborted");
          return;
        }

        console.error("[QUICK VIEW] Error loading product:", error);

        // Show error message
        this.loadingEl.style.display = "none";
        this.productDetailsEl.style.display = "block";
        this.productDetailsEl.innerHTML = `
          <div style="text-align: center; padding: 40px 20px;">
            <p style="color: #666; margin-bottom: 20px;">Sorry, we couldn't load the product details.</p>
            <a href="${productUrl}" class="btn btn--primary">View Full Product</a>
          </div>
        `;
      }
    }

    async prepareQuickViewContent(content, productUrl) {
      console.log("[QUICK VIEW] Preparing content for quick view...");
      console.log("[QUICK VIEW] Content element:", content);

      // Remove elements that shouldn't be in quick view
      const elementsToRemove = content.querySelectorAll(
        ".not-in-quickbuy, pickup-availability"
      );
      elementsToRemove.forEach((el) => el.remove());

      // Create new layout structure for quick view
      const quickViewContainer = document.createElement("div");
      quickViewContainer.className = "quick-view-layout";
      quickViewContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        padding: 20px;
      `;

      // Top row: Photo and Title/Price side by side
      const topRow = document.createElement("div");
      topRow.className = "quick-view-top-row";
      topRow.style.cssText = `
        display: flex;
        gap: 10px;
        padding-left: 20px;
        padding-right: 20px;
        align-items: flex-start;
      `;

      // Left side: Photo
      const photoSection = document.createElement("div");
      photoSection.style.cssText = `
        flex: 0 0 200px;
        max-width: 200px;
      `;

      // Get media gallery or product media collage
      const mediaGallery = content.querySelector(".media-gallery");
      const productMediaCollage = content.querySelector(
        ".product-media-collage"
      );

      console.log("[QUICK VIEW] Media gallery found:", !!mediaGallery);
      console.log(
        "[QUICK VIEW] Product media collage found:",
        !!productMediaCollage
      );

      if (mediaGallery) {
        // Clone media gallery and optimize for quick view
        const clonedGallery = mediaGallery.cloneNode(true);

        // Hide all images except the first one
        const allImages = clonedGallery.querySelectorAll(
          ".media-gallery__inner > *"
        );
        allImages.forEach((img, index) => {
          if (index > 0) {
            img.style.display = "none";
          }
        });

        // Hide thumbnail navigation
        const thumbnails = clonedGallery.querySelector(".thumbnails");
        if (thumbnails) {
          thumbnails.style.display = "none";
        }

        // Hide carousel navigation
        const carouselNav = clonedGallery.querySelector(
          ".carousel-slider-mobile-hidden"
        );
        if (carouselNav) {
          carouselNav.style.display = "none";
        }

        // Hide media gallery navigation
        const mediaNav = clonedGallery.querySelector(".media-gallery__nav");
        if (mediaNav) {
          mediaNav.style.display = "none";
        }

        // Ensure first image takes full width
        const firstImage = clonedGallery.querySelector(".main-image");
        if (firstImage) {
          firstImage.style.width = "100%";
          firstImage.style.height = "auto";
        }

        photoSection.appendChild(clonedGallery);
      } else if (productMediaCollage) {
        // Clone product media collage and optimize for quick view
        const clonedCollage = productMediaCollage.cloneNode(true);

        // Hide all collage items except the first one
        const allCollageItems = clonedCollage.querySelectorAll(
          ".product-media-collage__item"
        );
        allCollageItems.forEach((item, index) => {
          if (index > 0) {
            item.style.display = "none";
          }
        });

        // Ensure first collage item takes full width
        const firstCollageItem = clonedCollage.querySelector(
          ".product-media-collage__item:first-child"
        );
        if (firstCollageItem) {
          firstCollageItem.style.width = "100%";

          // Also ensure the wrapper and media take full width
          const mediaWrapper = firstCollageItem.querySelector(
            ".product-media-wrapper"
          );
          if (mediaWrapper) {
            mediaWrapper.style.width = "100%";
          }

          const media = firstCollageItem.querySelector(".product-media");
          if (media) {
            media.style.width = "100%";
          }
        }

        photoSection.appendChild(clonedCollage);
      }

      // Right side: Title and Price
      const titlePriceSection = document.createElement("div");
      titlePriceSection.style.cssText = `
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 15px;
      `;

      // Get title
      const titleBlock = content.querySelector(".title-row");
      console.log("[QUICK VIEW] Title block found:", !!titleBlock);
      if (titleBlock) {
        const clonedTitle = titleBlock.cloneNode(true);
        titlePriceSection.appendChild(clonedTitle);
      }

      // Get price
      const priceBlock = content.querySelector(".price-container");
      console.log("[QUICK VIEW] Price block found:", !!priceBlock);
      if (priceBlock) {
        const clonedPrice = priceBlock.cloneNode(true);
        titlePriceSection.appendChild(clonedPrice);
      }

      // Add photo and title/price to top row
      topRow.appendChild(photoSection);
      topRow.appendChild(titlePriceSection);

      // Bottom section: Swatches, variant pickers, add-to-cart, wishlist
      const bottomSection = document.createElement("div");
      bottomSection.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 15px;
      `;

      // Get color swatches (add them above variant picker)
      // Instead of cloning from PDP, we'll fetch the quick view swatches snippet
      await this.loadQuickViewSwatches(bottomSection, productUrl);

      // Get variant picker
      const variantPickerBlock = content.querySelector(".input-row");
      console.log("[QUICK VIEW] Variant picker found:", !!variantPickerBlock);
      if (variantPickerBlock) {
        const clonedVariantPicker = variantPickerBlock.cloneNode(true);
        bottomSection.appendChild(clonedVariantPicker);
      }

      // Get buy buttons (add to cart, etc.)
      const buyButtonsBlock = content.querySelector(".buy-buttons-row");
      console.log("[QUICK VIEW] Buy buttons found:", !!buyButtonsBlock);
      if (buyButtonsBlock) {
        const clonedBuyButtons = buyButtonsBlock.cloneNode(true);
        bottomSection.appendChild(clonedBuyButtons);
      }

      // Add top row and bottom section to main container
      quickViewContainer.appendChild(topRow);
      quickViewContainer.appendChild(bottomSection);

      console.log(
        "[QUICK VIEW] Final container children count:",
        quickViewContainer.children.length
      );
      console.log(
        "[QUICK VIEW] Photo section children:",
        photoSection.children.length
      );
      console.log(
        "[QUICK VIEW] Title/price section children:",
        titlePriceSection.children.length
      );
      console.log(
        "[QUICK VIEW] Bottom section children:",
        bottomSection.children.length
      );

      // Fix form IDs to be unique for quick view
      const forms = quickViewContainer.querySelectorAll("form");
      forms.forEach((form, index) => {
        const oldId = form.id;
        const newId = `quickview-${oldId}-${Date.now()}`;
        form.id = newId;

        // Update any labels or other elements that reference the form
        const referencingElements = quickViewContainer.querySelectorAll(
          `[form="${oldId}"]`
        );
        referencingElements.forEach((el) => {
          el.setAttribute("form", newId);
        });
      });

      // Fix variant picker form references
      const variantInputs = quickViewContainer.querySelectorAll(
        "variant-picker input"
      );
      variantInputs.forEach((input) => {
        if (input.hasAttribute("form")) {
          const oldFormId = input.getAttribute("form");
          const newFormId = `quickview-${oldFormId}-${Date.now()}`;
          input.setAttribute("form", newFormId);
        }
      });

      // Modify links to point to full product page
      const links = quickViewContainer.querySelectorAll("a[href]");
      links.forEach((link) => {
        if (
          link.href.includes("/products/") &&
          !link.href.includes("#") &&
          !link.classList.contains("variant-picker__option")
        ) {
          link.href = productUrl;
        }
      });

      // Ensure product titles link to full page
      const productTitles =
        quickViewContainer.querySelectorAll(".product-title");
      productTitles.forEach((title) => {
        if (!title.querySelector("a")) {
          title.innerHTML = `<a href="${productUrl}">${title.innerHTML}</a>`;
        }
      });

      // Keep variant picker scripts but remove others that might cause issues
      const scripts = quickViewContainer.querySelectorAll("script");
      scripts.forEach((script) => {
        if (!script.type || script.type !== "application/json") {
          script.remove();
        }
      });

      return quickViewContainer;
    }

    async loadQuickViewSwatches(container, productUrl) {
      try {
        // Fetch the product page to get the quick view swatches snippet
        const response = await fetch(productUrl);
        const html = await response.text();

        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Look for the hidden quick view swatches container
        const quickViewContainer = doc.querySelector(
          ".quick-view-swatches-container"
        );

        if (quickViewContainer) {
          // Clone the entire container content
          const clonedContent = quickViewContainer.cloneNode(true);

          // Remove the display: none style
          clonedContent.style.display = "";

          // Append to our container
          container.appendChild(clonedContent);

          // Fix swatch links to work with quick view
          this.fixQuickViewSwatchLinks(clonedContent);
        }
      } catch (error) {
        console.error("[QUICK VIEW] Error loading quick view swatches:", error);
      }
    }

    fixQuickViewSwatchLinks(swatchesContainer) {
      // Find all swatch links
      const swatchLinks = swatchesContainer.querySelectorAll(
        ".byte-product-swatch-item"
      );

      swatchLinks.forEach((link) => {
        const productUrl = link.getAttribute("href");
        const tooltip = link.getAttribute("data-tooltip");

        // Remove the href to prevent navigation
        link.removeAttribute("href");

        // Add click event listener
        link.addEventListener("click", (e) => {
          e.preventDefault();

          // Close current quick view
          this.close();

          // Navigate to the product's PDP
          setTimeout(() => {
            if (productUrl) {
              window.location.href = productUrl;
            }
          }, 300);
        });

        // Add cursor pointer style
        link.style.cursor = "pointer";

        // Add hover effect
        link.addEventListener("mouseenter", () => {
          link.style.opacity = "0.8";
        });

        link.addEventListener("mouseleave", () => {
          link.style.opacity = "1";
        });
      });
    }

    initializeQuickViewScripts() {
      console.log("[QUICK VIEW] Initializing scripts...");

      try {
        // Initialize any theme scripts that might be needed
        if (typeof window.theme !== "undefined" && window.theme.initQuickView) {
          window.theme.initQuickView();
        }
      } catch (error) {
        console.log("[QUICK VIEW] Theme initQuickView failed:", error);
      }

      try {
        // Force re-initialize variant picker in quick view
        const variantPicker = this.popup.querySelector("variant-picker");
        if (variantPicker && !variantPicker.hasAttribute("loaded")) {
          // Manually trigger the variant picker constructor
          if (customElements.get("variant-picker")) {
            // Create a new instance of variant picker
            const VariantPickerClass = customElements.get("variant-picker");
            const newInstance = new VariantPickerClass();

            // Copy properties from the existing element
            Object.setPrototypeOf(variantPicker, VariantPickerClass.prototype);
            VariantPickerClass.call(variantPicker);
          }
        }
      } catch (error) {
        console.log(
          "[QUICK VIEW] Variant picker initialization failed:",
          error
        );
      }

      try {
        // Trigger any custom events
        this.popup.dispatchEvent(
          new CustomEvent("on:quickview:after-open", {
            bubbles: true,
            detail: { popup: this.popup },
          })
        );
      } catch (error) {
        console.log("[QUICK VIEW] Custom event dispatch failed:", error);
      }

      console.log("[QUICK VIEW] Script initialization completed");
    }

    fixVariantSync() {
      console.log("[QUICK VIEW] Starting variant sync fix...");

      // Find the product form in the quick view
      const productForm = this.popup.querySelector(".js-product-form");
      if (!productForm) {
        console.log("[QUICK VIEW] No product form found");
        return;
      }

      // Listen for variant changes
      const variantPicker = this.popup.querySelector("variant-picker");
      if (variantPicker) {
        console.log("[QUICK VIEW] Found variant picker, setting up listeners");

        // Listen for variant change events
        variantPicker.addEventListener("on:variant:change", (event) => {
          console.log("[QUICK VIEW] Variant change detected:", event.detail);

          if (event.detail.variant) {
            const hiddenInput = productForm.querySelector('input[name="id"]');
            if (hiddenInput) {
              const oldValue = hiddenInput.value;
              hiddenInput.value = event.detail.variant.id;
              console.log(
                `[QUICK VIEW] Updated variant ID: ${oldValue} → ${event.detail.variant.id}`
              );
              console.log(
                `[QUICK VIEW] New variant: ${event.detail.variant.title}`
              );
            }
          }
        });

        // Also listen for input changes directly
        const radioInputs = variantPicker.querySelectorAll(
          'input[type="radio"]'
        );
        radioInputs.forEach((input) => {
          input.addEventListener("change", () => {
            console.log("[QUICK VIEW] Radio input changed:", input.value);
            setTimeout(() => {
              this.syncVariantFromSelection();
            }, 50);
          });
        });
      }

      // Override form submission to ensure correct variant
      productForm.addEventListener("submit", (e) => {
        e.preventDefault();
        console.log("[QUICK VIEW] Form submission intercepted and prevented");

        // Force sync variant before submission
        this.syncVariantFromSelection();

        // Wait a moment then submit with correct variant
        setTimeout(() => {
          this.submitFormWithCorrectVariant(productForm);
        }, 100);
      });
    }

    syncVariantFromSelection() {
      const productForm = this.popup.querySelector(".js-product-form");
      const variantPicker = this.popup.querySelector("variant-picker");

      if (!productForm || !variantPicker) return;

      const hiddenInput = productForm.querySelector('input[name="id"]');
      const selectedInputs = Array.from(
        variantPicker.querySelectorAll('input[type="radio"]:checked')
      );

      if (selectedInputs.length === 0) {
        console.log("[QUICK VIEW] No options selected");
        return;
      }

      const selectedValues = selectedInputs.map((input) => input.value);
      console.log("[QUICK VIEW] Selected values:", selectedValues);

      // Get variant data from the script tag
      const variantScript = variantPicker.querySelector(
        'script[type="application/json"]'
      );
      if (variantScript) {
        try {
          const data = JSON.parse(variantScript.textContent);
          console.log("[QUICK VIEW] Variant data:", data);

          // Find the matching variant
          const matchingVariant = data.variants.find((variant) => {
            const matches = variant.options.every(
              (option, index) => option === selectedValues[index]
            );
            console.log(
              `[QUICK VIEW] Checking variant ${variant.id} (${variant.title}): ${matches}`
            );
            return matches;
          });

          if (matchingVariant && hiddenInput) {
            const oldValue = hiddenInput.value;
            hiddenInput.value = matchingVariant.id;
            console.log(
              `[QUICK VIEW] FINAL SYNC: ${oldValue} → ${matchingVariant.id} (${matchingVariant.title})`
            );
          } else {
            console.log(
              "[QUICK VIEW] No matching variant found or no hidden input"
            );
          }
        } catch (error) {
          console.error("[QUICK VIEW] Error parsing variant data:", error);
        }
      }
    }

    async submitFormWithCorrectVariant(form) {
      const hiddenInput = form.querySelector('input[name="id"]');
      const quantityInput = form.querySelector('input[name="quantity"]');

      console.log(
        "[QUICK VIEW] Submitting with variant ID:",
        hiddenInput?.value
      );
      console.log("[QUICK VIEW] Quantity:", quantityInput?.value || 1);

      if (!hiddenInput?.value) {
        console.error("[QUICK VIEW] No variant selected");
        return;
      }

      try {
        // Prepare form data
        const formData = new FormData();
        formData.append("id", hiddenInput.value);
        formData.append("quantity", quantityInput?.value || 1);

        // Add any custom properties
        const customInputs = form.querySelectorAll('input[name^="properties"]');
        customInputs.forEach((input) => {
          if (input.type === "checkbox") {
            formData.append(input.name, input.checked ? input.value : "");
          } else {
            formData.append(input.name, input.value);
          }
        });

        // Submit to cart
        const response = await fetch("/cart/add.js", {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const result = await response.json();
          console.log("[QUICK VIEW] Successfully added to cart:", result);

          // Close popup
          this.close();

          // Trigger cart update events to refresh cart without page reload
          document.dispatchEvent(
            new CustomEvent("cart:added", {
              detail: {
                variant_id: hiddenInput.value,
                quantity: quantityInput?.value || 1,
              },
            })
          );

          // Trigger cart refresh events
          document.dispatchEvent(
            new CustomEvent("dispatch:cart-drawer:refresh")
          );

          // Also trigger a custom cart update event
          document.dispatchEvent(
            new CustomEvent("cart:updated", {
              detail: { cart: result },
            })
          );

          // Open cart drawer if enabled
          document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
        } else {
          const error = await response.text();
          console.error("[QUICK VIEW] Add to cart failed:", error);
          alert("Ürün sepete eklenemedi. Lütfen tekrar deneyin.");
        }
      } catch (error) {
        console.error("[QUICK VIEW] Error adding to cart:", error);
        alert("Bir hata oluştu. Lütfen tekrar deneyin.");
      }
    }

    close() {
      if (!this.isOpen) return;

      console.log("[QUICK VIEW] Closing popup");

      // Cancel any ongoing request
      if (this.currentRequest) {
        this.currentRequest.abort();
        this.currentRequest = null;
      }

      // Hide popup
      this.popup.classList.remove("active");
      this.isOpen = false;

      // Clear content
      setTimeout(() => {
        this.loadingEl.style.display = "flex";
        this.productDetailsEl.style.display = "none";
        this.productDetailsEl.innerHTML = "";
      }, 300);

      // Trigger close event
      this.popup.dispatchEvent(
        new CustomEvent("on:quickview:after-close", {
          bubbles: true,
          detail: { popup: this.popup },
        })
      );
    }
  }

  // Initialize Quick View when DOM is ready
  document.addEventListener("DOMContentLoaded", () => {
    window.QuickViewPopup = new QuickViewPopup();
  });

  // =================================================================================================
  // Add to Cart Button Functionality - Update button text and state based on variant availability
  // =================================================================================================

  // Function to update add to cart button text and state
  function updateAddToCartButton(button, variant = null) {
    if (!button) return;

  // Kilitli durum: OOS olarak işaretlendiyse asla tekrar etkinleştirme
  if (button.dataset.lockState === 'oos') {
    button.disabled = true;
    button.textContent = "Tükendi";
    button.setAttribute("aria-disabled", "true");
    button.style.opacity = "0.6";
    button.style.cursor = "not-allowed";
    button.className = "btn btn--large add-to-cart custom-add-to-cart-style disabled out-of-stock";
    return;
  }

    // Get button text elements
    const buttonText = button.textContent?.trim();
    const dataText = button.getAttribute("data-add-to-cart-text");

    // Default text
    let newText = "Sepete Ekle";
    let isDisabled = false;
    let buttonClasses = [
      "btn",
      "btn--large",
      "add-to-cart",
      "custom-add-to-cart-style",
    ];

    // Check if we have variant information
    if (variant) {
      // Check availability
      if (!variant.available) {
        newText = "Tükendi";
        isDisabled = true;
        buttonClasses.push("disabled", "out-of-stock");
      } else {
        // Check inventory quantity
        if (
          variant.inventory_quantity !== null &&
          variant.inventory_quantity <= 0
        ) {
          newText = "Stokta Yok";
          isDisabled = true;
          buttonClasses.push("disabled", "no-inventory");
        } else {
          // Check if it's a preorder product
          if (
            variant.requires_shipping === false ||
            variant.inventory_policy === "continue"
          ) {
            newText = "Ön Sipariş";
            buttonClasses.push("preorder");
          } else {
            newText = "Sepete Ekle";
            buttonClasses.push("available");
          }
        }
      }
    } else {
      // No variant info, check if product is available
      const productForm = button.closest("form");
      if (productForm) {
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) {
          const variantId = variantInput.value;
          console.log("🔍 No variant object, checking variant ID:", variantId);

          // Try to get variant info from the page
          const variantScript = document.querySelector(
            'script[type="application/json"][data-product-json]'
          );
          if (variantScript) {
            try {
              const productData = JSON.parse(variantScript.textContent);
              const foundVariant = productData.variants?.find(
                (v) => v.id.toString() === variantId
              );
              if (foundVariant) {
                updateAddToCartButton(button, foundVariant);
                return;
              }
            } catch (e) {
              console.log("❌ Error parsing product JSON:", e);
            }
          }
        }
      }
    }

    // Update button text
    button.textContent = newText;

    // Update data attribute
    button.setAttribute("data-add-to-cart-text", newText);

    // Update button state
    button.disabled = isDisabled;

    // Update classes
    button.className = buttonClasses.join(" ");

    // Update ARIA attributes
    if (isDisabled) {
      button.setAttribute("aria-disabled", "true");
      button.setAttribute(
        "aria-label",
        `${newText} - Bu ürün şu anda mevcut değil`
      );
    } else {
      button.removeAttribute("aria-disabled");
      button.setAttribute(
        "aria-label",
        `${newText} - ${variant?.title || "Ürün"}`
      );
    }

    // Add visual feedback
    if (isDisabled) {
      button.style.opacity = "0.6";
      button.style.cursor = "not-allowed";
    } else {
      button.style.opacity = "1";
      button.style.cursor = "pointer";
    }
  }

  // Function to find and update all add to cart buttons
  function updateAllAddToCartButtons() {
    const addToCartButtons = document.querySelectorAll(
      '.add-to-cart, [data-add-to-cart-text], button[name="add"]'
    );

    if (addToCartButtons.length === 0) {
      console.log("❌ No Add to Cart buttons found");
      return;
    }

  addToCartButtons.forEach((button, index) => {
    // Quick view içindeki görünür Tükendi rozeti varsa butonu kilitle
    const quickViewRoot = button.closest('.quick-view-product-details');
    const hasSoldOutBadge = !!(quickViewRoot && quickViewRoot.querySelector('.product-badge img[alt*="Tükendi"], .product-badge [aria-label*="Tükendi"]'));
    if (hasSoldOutBadge) {
      button.dataset.lockState = 'oos';
      button.disabled = true;
      button.textContent = "Tükendi";
      button.setAttribute("aria-disabled", "true");
      button.style.opacity = "0.6";
      button.style.cursor = "not-allowed";
      button.className = "btn btn--large add-to-cart custom-add-to-cart-style disabled out-of-stock";
      return;
    }
      // Get current variant info
      const productForm = button.closest("form");
      let currentVariant = null;

      if (productForm) {
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) {
          const variantId = variantInput.value;

          // Try to get variant from variant-picker script first
          const variantScript = document.querySelector(
            'variant-picker script[type="application/json"]'
          );
          if (variantScript) {
            try {
              const variantData = JSON.parse(variantScript.textContent);
              currentVariant = variantData.variants?.find(
                (v) => v.id.toString() === variantId
              );
            } catch (e) {
              console.log("❌ Error parsing variant-picker JSON:", e);
            }
          }

          // If not found, try other product JSON scripts
          if (!currentVariant) {
            const productScripts = document.querySelectorAll(
              'script[type="application/json"]'
            );
            for (const script of productScripts) {
              try {
                const data = JSON.parse(script.textContent);
                if (data.variants && data.variants.length > 0) {
                  currentVariant = data.variants.find(
                    (v) => v.id.toString() === variantId
                  );
                  if (currentVariant) {
                    console.log(
                      "✅ Found variant from other script:",
                      currentVariant
                    );
                    break;
                  }
                }
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }

          // If still not found, try to get from the page context
          if (!currentVariant) {
            console.log(
              "🔍 Variant not found in JSON, checking page context..."
            );
            // Look for variant info in the page HTML
            const variantElements = document.querySelectorAll(
              "[data-variant-id], [data-variant]"
            );
            for (const element of variantElements) {
              const elementVariantId =
                element.getAttribute("data-variant-id") ||
                element.getAttribute("data-variant");
              if (elementVariantId === variantId) {
                // Try to extract variant info from the element
                const available =
                  element.getAttribute("data-available") !== "false";
                const inventoryQuantity = element.getAttribute(
                  "data-inventory-quantity"
                );
                const title =
                  element.getAttribute("data-variant-title") ||
                  "Unknown Variant";

                currentVariant = {
                  id: variantId,
                  title: title,
                  available: available,
                  inventory_quantity: inventoryQuantity
                    ? parseInt(inventoryQuantity)
                    : null,
                };
                console.log(
                  "✅ Created variant from page context:",
                  currentVariant
                );
                break;
              }
            }
          }
        }
      }

      // If no variant found, check product availability
      if (!currentVariant) {
        console.log("❌ No variant found, checking product availability...");
        
        // Check if button is already disabled in HTML (from Liquid)
        if (button.hasAttribute('disabled') || button.disabled) {
          console.log("❌ Button is already disabled in HTML, keeping disabled...");
          button.disabled = true;
          button.textContent = "Tükendi";
          button.setAttribute("aria-disabled", "true");
          button.style.opacity = "0.6";
          button.style.cursor = "not-allowed";
          button.className = "btn btn--large add-to-cart custom-add-to-cart-style disabled out-of-stock";
          return;
        }
        
        // Check if product is available from the page and evaluate stock of target variant
        const productScript = document.querySelector('script[type="application/json"][data-product-json]');
        if (productScript) {
          try {
            const productData = JSON.parse(productScript.textContent);
            if (productData.product && !productData.product.available) {
              console.log("❌ Product is not available, disabling button...");
              button.dataset.lockState = 'oos';
              button.disabled = true;
              button.textContent = "Tükendi";
              button.setAttribute("aria-disabled", "true");
              button.style.opacity = "0.6";
              button.style.cursor = "not-allowed";
              button.className = "btn btn--large add-to-cart custom-add-to-cart-style disabled out-of-stock";
              return;
            }

            // Determine target variant and its inventory when no explicit variant object was found
            const variants = productData.variants || productData.product?.variants || [];
            const form = button.closest('form');
            const idInput = form ? form.querySelector('[name="id"]') : null;
            const selectedVariantId = idInput ? idInput.value : null;

            let targetVariant = null;
            if (selectedVariantId && variants.length > 0) {
              targetVariant = variants.find(v => v.id && v.id.toString() === selectedVariantId.toString()) || null;
            }
            if (!targetVariant && variants.length === 1) {
              targetVariant = variants[0];
            }

            if (targetVariant) {
              const qty = typeof targetVariant.inventory_quantity === 'number' ? targetVariant.inventory_quantity : null;
              const available = targetVariant.available !== false;

              if (qty !== null) {
                if (qty > 0 && available) {
                  const labelText = button.getAttribute("data-add-to-cart-text") || "Sepete Ekle";
                  button.disabled = false;
                  button.textContent = labelText;
                  button.removeAttribute("aria-disabled");
                  button.style.opacity = "1";
                  button.style.cursor = "pointer";
                  button.className = "btn btn--large add-to-cart custom-add-to-cart-style";
                  return;
                } else {
                  button.disabled = true;
                  button.textContent = "Tükendi";
                  button.setAttribute("aria-disabled", "true");
                  button.style.opacity = "0.6";
                  button.style.cursor = "not-allowed";
                  button.className = "btn btn--large add-to-cart custom-add-to-cart-style disabled out-of-stock";
                  button.dataset.lockState = 'oos';
                  return;
                }
              } else {
                // No explicit quantity, fall back to available
                if (available) {
                  const labelText = button.getAttribute("data-add-to-cart-text") || "Sepete Ekle";
                  button.disabled = false;
                  button.textContent = labelText;
                  button.removeAttribute("aria-disabled");
                  button.style.opacity = "1";
                  button.style.cursor = "pointer";
                  button.className = "btn btn--large add-to-cart custom-add-to-cart-style";
                  return;
                } else {
                  button.disabled = true;
                  button.textContent = "Tükendi";
                  button.setAttribute("aria-disabled", "true");
                  button.style.opacity = "0.6";
                  button.style.cursor = "not-allowed";
                  button.className = "btn btn--large add-to-cart custom-add-to-cart-style disabled out-of-stock";
                  button.dataset.lockState = 'oos';
                  return;
                }
              }
            }
          } catch (e) {
            console.log("❌ Error parsing product JSON:", e);
          }
        }
        
        // If we still cannot decide from page context, try fetching this variant only (quick and cached)
        const form = button.closest('form');
        const idInput = form ? form.querySelector('[name="id"]') : null;
        const variantIdForFetch = idInput ? idInput.value : null;
        if (variantIdForFetch) {
          fetch(`/variants/${variantIdForFetch}.json`, { credentials: 'same-origin' })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              const v = data?.variant || null;
              if (v) {
                const qty = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null;
                const available = v.available !== false;
                if (qty !== null) {
                  if (qty > 0 && available) {
                    const labelText = button.getAttribute("data-add-to-cart-text") || "Sepete Ekle";
                    button.disabled = false;
                    button.textContent = labelText;
                    button.removeAttribute("aria-disabled");
                    button.style.opacity = "1";
                    button.style.cursor = "pointer";
                    button.className = "btn btn--large add-to-cart custom-add-to-cart-style";
                  } else {
                    button.disabled = true;
                    button.textContent = "Tükendi";
                    button.setAttribute("aria-disabled", "true");
                    button.style.opacity = "0.6";
                    button.style.cursor = "not-allowed";
                    button.className = "btn btn--large add-to-cart custom-add-to-cart-style disabled out-of-stock";
                    button.dataset.lockState = 'oos';
                  }
                  return;
                }
              }
              // Son çare: durumu değiştirme
            })
            .catch(() => { /* no-op, mevcut durumu koru */ });
          return;
        }

        // Son çare: hiçbir sinyal yoksa mevcut durumu koru
      } else {
        updateAddToCartButton(button, currentVariant);
      }
    });

    // Also update variant option availability classes in quick view
    try {
      updateQuickViewOptionAvailability();
    } catch (e) {
      // no-op
    }
  }

  // Function to monitor variant changes and update buttons
  function monitorVariantChanges() {
    // Listen for variant change events
    document.addEventListener("on:variant:change", (event) => {
      console.log("🔄 Variant change detected:", event.detail);

      if (event.detail.variant) {
        // Update all buttons with new variant info
        setTimeout(() => {
          updateAllAddToCartButtons();
          try { updateQuickViewOptionAvailability(); } catch (e) {}
        }, 100);
      }
    });

    // Also monitor form input changes
    document.addEventListener("change", (event) => {
      if (
        event.target.name === "id" ||
        event.target.closest("variant-picker")
      ) {
        console.log("🔄 Variant input changed:", event.target);

        setTimeout(() => {
          updateAllAddToCartButtons();
          try { updateQuickViewOptionAvailability(); } catch (e) {}
        }, 200);
      }
    });

    // Monitor for variant picker changes
    const variantPickers = document.querySelectorAll("variant-picker");
    variantPickers.forEach((picker) => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            setTimeout(() => {
              updateAllAddToCartButtons();
            }, 100);
          }
        });
      });

      observer.observe(picker, {
        attributes: true,
        attributeFilter: ["class"],
      });
    });

    // Monitor for radio button changes in variant picker
    document.addEventListener("change", (event) => {
      if (
        event.target.type === "radio" &&
        event.target.closest("variant-picker")
      ) {
        console.log("🔄 Variant radio button changed:", event.target.value);

        setTimeout(() => {
          updateAllAddToCartButtons();
          try { updateQuickViewOptionAvailability(); } catch (e) {}
        }, 100);
      }
    });

    // Monitor for any clicks on variant options
    document.addEventListener("click", (event) => {
      if (
        event.target.closest("variant-picker") &&
        (event.target.type === "radio" ||
          event.target.closest('input[type="radio"]'))
      ) {
        console.log("🔄 Variant option clicked:", event.target);

        setTimeout(() => {
          updateAllAddToCartButtons();
          try { updateQuickViewOptionAvailability(); } catch (e) {}
        }, 150);
      }
    });

    // Monitor for URL changes (in case of variant URL updates)
    let currentUrl = window.location.href;
    setInterval(() => {
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;
        setTimeout(() => {
          updateAllAddToCartButtons();
          try { updateQuickViewOptionAvailability(); } catch (e) {}
        }, 200);
      }
    }, 1000);
  }

  // Compute per-option availability in quick view and toggle is-unavailable class on inputs
  function updateQuickViewOptionAvailability() {
    const quickView = document.querySelector('.quick-view-product-details');
    if (!quickView) return;

    const variantPicker = quickView.querySelector('variant-picker');
    if (!variantPicker) return;

    const dataScript = variantPicker.querySelector('script[type="application/json"]');
    if (!dataScript) return;

    let data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (_) {
      return;
    }

    const optionSelectors = variantPicker.querySelectorAll('.option-selector:not(.option-selector--custom)');

    // Determine currently selected values
    const selectedValues = Array.from(optionSelectors).map((selector) => {
      if (selector.dataset.selectorType === 'dropdown') {
        const sel = selector.querySelector('.custom-select__option[aria-selected="true"]');
        return sel ? sel.dataset.value : null;
      } else {
        const sel = selector.querySelector('input.js-option:checked');
        return sel ? sel.value : null;
      }
    });

    optionSelectors.forEach((selector, selectorIndex) => {
      const inputs = selector.querySelectorAll('.js-option:not([data-value=""])');
      inputs.forEach((input) => {
        const optionValue = selector.dataset.selectorType === 'dropdown' ? input.dataset.value : input.value;
        let variantsExist = false;
        let variantsAvailable = false;

        data.variants.forEach((v) => {
          // Check previous selections match, except current selector index
          let matches = 0;
          for (let i = 0; i < optionSelectors.length; i += 1) {
            if (i === selectorIndex) continue;
            const selVal = selectedValues[i];
            if (selVal === null || v.options[i] === selVal) {
              matches += 1;
            }
          }

          if (v.options[selectorIndex] === optionValue && matches === optionSelectors.length - 1) {
            variantsExist = true;
            if (v.available) variantsAvailable = true;
          }
        });

        // Toggle same classes the theme expects
        input.classList.toggle('is-nonexistent', !variantsExist);
        input.classList.toggle('is-unavailable', !variantsAvailable);
      });
    });
  }

  // Function to check product availability from page data
  function checkProductAvailability() {
    // Look for product JSON data in variant-picker script
    const variantScripts = document.querySelectorAll(
      'variant-picker script[type="application/json"]'
    );

    variantScripts.forEach((script) => {
      try {
        const data = JSON.parse(script.textContent);
        if (data.variants && data.variants.length > 0) {
          // Update buttons with this data
          setTimeout(() => {
            updateAllAddToCartButtons();
          }, 500);
        }
      } catch (e) {
        console.log("❌ Error parsing variant JSON:", e);
      }
    });

    // Also look for any other product JSON data
    const productScripts = document.querySelectorAll(
      'script[type="application/json"]'
    );

    productScripts.forEach((script) => {
      try {
        const data = JSON.parse(script.textContent);
        if (data.product || (data.variants && data.variants.length > 0)) {
          // Update buttons with this data
          setTimeout(() => {
            updateAllAddToCartButtons();
          }, 500);
        }
      } catch (e) {
        // Not valid JSON, skip
      }
    });
  }

  // Initialize button functionality when DOM is ready
  document.addEventListener("DOMContentLoaded", () => {
    // Initial update - try multiple times
    const initialUpdate = () => {
      updateAllAddToCartButtons();
    };

    // Try immediately
    initialUpdate();

    // Try after a short delay
    setTimeout(initialUpdate, 500);

    // Try after a longer delay
    setTimeout(initialUpdate, 1000);

    // Check for product data
    checkProductAvailability();

    // Monitor variant changes
    monitorVariantChanges();

    // Update again after delays to catch any dynamic changes
    setTimeout(() => {
      updateAllAddToCartButtons();
    }, 2000);

    setTimeout(() => {
      updateAllAddToCartButtons();
    }, 5000);

    // Also try to update when window loads completely
    window.addEventListener("load", () => {
      setTimeout(() => {
        updateAllAddToCartButtons();
      }, 500);
    });

    // Monitor for any dynamic content changes
    const bodyObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any add to cart buttons were added
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (
                node.classList?.contains("add-to-cart") ||
                node.querySelector?.(".add-to-cart") ||
                node.querySelector?.('button[name="add"]')
              ) {
                shouldUpdate = true;
              }
            }
          });
        }
      });

      if (shouldUpdate) {
        setTimeout(() => {
          updateAllAddToCartButtons();
        }, 200);
      }
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });

  // Also update when cart drawer opens (for dynamic content)
  document.addEventListener("cart:drawer:open", () => {
    setTimeout(() => {
      updateAllAddToCartButtons();
    }, 500);
  });

  // Update on any cart-related events
  document.addEventListener("on:cart:add", () => {
    setTimeout(() => {
      updateAllAddToCartButtons();
    }, 100);
  });

  document.addEventListener("on:cart:error", () => {
    setTimeout(() => {
      updateAllAddToCartButtons();
    }, 100);
  });

  // Add manual trigger functions to global scope
  window.updateAddToCartButtons = function () {
    updateAllAddToCartButtons();
  };

  window.updateAddToCartButton = function (button, variant) {
    updateAddToCartButton(button, variant);
  };

  // Add a function to debug variant information
  window.debugVariantInfo = function () {
    // Check variant-picker script
    const variantScript = document.querySelector(
      'variant-picker script[type="application/json"]'
    );
    if (variantScript) {
      try {
        const data = JSON.parse(variantScript.textContent);

        if (data.variants) {
          data.variants.forEach((variant, index) => {
            console.log(
              `  ${index + 1}. ID: ${variant.id}, Title: ${
                variant.title
              }, Available: ${variant.available}, Inventory: ${
                variant.inventory_quantity
              }`
            );
          });
        }
      } catch (e) {
        console.log("❌ Error parsing variant-picker JSON:", e);
      }
    } else {
      console.log("❌ No variant-picker script found");
    }

    // Check current form
    const forms = document.querySelectorAll("form");
    forms.forEach((form, formIndex) => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        console.log(
          `📝 Form ${formIndex + 1}: Variant ID = ${variantInput.value}`
        );
      }
    });

    // Check add to cart buttons
    const buttons = document.querySelectorAll(
      '.add-to-cart, [data-add-to-cart-text], button[name="add"]'
    );

    buttons.forEach((button, index) => {
      console.log(`  Button ${index + 1}:`, {
        text: button.textContent?.trim(),
        disabled: button.disabled,
        classes: button.className,
        variantId: button.closest("form")?.querySelector('input[name="id"]')
          ?.value,
      });
    });
  };

  // Add a function to find and highlight all add to cart buttons
  window.highlightAddToCartButtons = function () {
    const buttons = document.querySelectorAll(
      '.add-to-cart, [data-add-to-cart-text], button[name="add"]'
    );

    buttons.forEach((button, index) => {
      const originalBorder = button.style.border;
      const originalBackground = button.style.backgroundColor;

      button.style.border = "3px solid red";
      button.style.backgroundColor = "yellow";

      setTimeout(() => {
        button.style.border = originalBorder;
        button.style.backgroundColor = originalBackground;
      }, 3000);
    });
  };

  // Check if read more functionality is needed
  function checkIfReadMoreNeeded(container, content, button) {
    // Temporarily remove max-height to get natural height
    const originalMaxHeight = content.style.maxHeight;
    const originalOverflow = content.style.overflow;

    content.style.maxHeight = "none";
    content.style.overflow = "visible";

    const naturalHeight = content.scrollHeight;

    // Restore original styles
    content.style.maxHeight = originalMaxHeight;
    content.style.overflow = originalOverflow;

    // If natural height is greater than 300px, show read more button
    if (naturalHeight > 300) {
      container.classList.add("needs-read-more");
    } else {
      // If content is short, remove max-height restriction and hide button
      content.style.maxHeight = "none";
      content.style.overflow = "visible";
      button.style.display = "none";
      // Remove the needs-read-more class to hide gradient overlay
      container.classList.remove("needs-read-more");
    }
  }

  // Read More Button Functionality
  function initReadMoreButtons() {
    document
      .querySelectorAll('[data-read-more="true"]')
      .forEach((container) => {
        const button = container.querySelector(".read-more-button");
        const content = container.querySelector(".read-more-content");
        const readMoreText = container.querySelector(".read-more-text");
        const readLessText = container.querySelector(".read-less-text");

        if (!button || !content) return;

        // Check if content actually needs read more functionality
        checkIfReadMoreNeeded(container, content, button);

        // Remove existing listener and add new one
        button.removeEventListener("click", button._readMoreHandler);
        button._readMoreHandler = () => {
          const isExpanded = container.classList.contains("expanded");

          if (isExpanded) {
            container.classList.remove("expanded");
            content.style.cssText =
              "max-height: 300px; height: 300px; overflow: hidden;";
            readMoreText.style.display = "inline";
            readLessText.style.display = "none";
          } else {
            container.classList.add("expanded");
            content.style.cssText =
              "max-height: 100%; height: 100%; overflow: visible;";
            readMoreText.style.display = "none";
            readLessText.style.display = "inline";
          }
        };

        button.addEventListener("click", button._readMoreHandler);
      });
  }

  // Initialize read more buttons when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initReadMoreButtons);
  } else {
    initReadMoreButtons();
  }

  // =================================================================================================
  // Infinity Scroll Functionality
  // =================================================================================================

  class InfinityScroll {
    constructor() {
      this.currentPage = 1;
      this.hasNextPage = true;
      this.isLoadingNextPage = false;
      this.isLoading = false;
      this.sentinel = null;
      this.observer = null;
      
      this.init();
    }

    init() {
      // Check if infinity scroll is enabled
      const paginationType = document.querySelector("[data-collection-container]")?.dataset.paginateType;
      if (paginationType !== 'infinite') {
        console.log('[INFINITY SCROLL] Infinity scroll not enabled');
        return;
      }

      console.log('[INFINITY SCROLL] Initializing infinity scroll');
      this.setupInfiniteScroll();
    }

    setupInfiniteScroll() {
      const productGrid = document.querySelector("[data-product-grid]");
      if (!productGrid) return;

      const totalProducts = parseInt(productGrid.dataset.totalProducts);
      const productsPerPage = parseInt(
        document.querySelector("[data-collection-container]")?.dataset.productsPerPage,
      );
      const paginationType = document.querySelector(
        "[data-collection-container]",
      )?.dataset.paginateType;

      if (document.querySelector(".pagination") && paginationType !== "click") {
        document.querySelector(".pagination").classList.add("hidden");
      }

      if (totalProducts <= productsPerPage || paginationType === "click") {
        const loader = document.querySelector("[data-infinite-scroll-loader]");
        if (loader) {
          loader.classList.add("hidden");
        }
        return;
      }

      const totalPages = Math.ceil(totalProducts / productsPerPage);
      if (this.currentPage >= totalPages) {
        this.hasNextPage = false;
        const loader = document.querySelector("[data-infinite-scroll-loader]");
        if (loader) {
          loader.classList.add("hidden");
        }
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              this.hasNextPage &&
              !this.isLoadingNextPage &&
              !this.isLoading
            ) {
              this.loadNextPage();
            }
          });
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0,
        },
      );

      this.sentinel = document.createElement("div");
      this.sentinel.setAttribute("data-infinite-scroll-sentinel", "");
      productGrid.after(this.sentinel);

      this.observer.observe(this.sentinel);

      const loader = document.querySelector("[data-infinite-scroll-loader]");
      loader?.classList.remove("hidden");
    }

    async loadNextPage() {
      const productGrid = document.querySelector("[data-product-grid]");
      if (!productGrid) return;

      const totalProducts = parseInt(productGrid.dataset.totalProducts);
      const productsPerPage = parseInt(
        document.querySelector("[data-collection-container]")?.dataset.productsPerPage,
      );
      const paginationType = document.querySelector(
        "[data-collection-container]",
      )?.dataset.paginateType;

      const totalPages = Math.ceil(totalProducts / productsPerPage);

      if (this.currentPage >= totalPages) {
        this.hasNextPage = false;
        const loader = document.querySelector("[data-infinite-scroll-loader]");
        if (loader) {
          loader.classList.add("hidden");
        }
        return;
      }

      this.isLoadingNextPage = true;
      const loader = document.querySelector("[data-infinite-scroll-loader]");

      if (loader) {
        loader.style.opacity = "1";
        loader.classList.remove("hidden");
      }

      try {
        const url = new URL(window.location.href);
        this.currentPage++;
        url.searchParams.set("page", this.currentPage);

        console.log('[INFINITY SCROLL] Loading page:', this.currentPage, 'from:', url.toString());

        const response = await Promise.race([
          fetch(url.toString(), {
            headers: {
              Accept: "text/html",
            },
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Timeout")), 10000),
          ),
        ]);

        if (!response.ok) throw new Error("Network response was not ok");

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newProducts = doc.querySelector(
          "[data-product-grid-items]",
        )?.children;

        if (!newProducts || newProducts.length === 0) {
          this.hasNextPage = false;
          if (loader) loader.classList.add("hidden");
          return;
        }

        const grid = document.querySelector("[data-product-grid-items]");

        const productPromises = Array.from(newProducts).map(async (product) => {
          const images = product.querySelectorAll("img");
          await Promise.all(
            Array.from(images).map((img) => {
              if (img.complete) return Promise.resolve();
              return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              });
            }),
          );
          return product.cloneNode(true);
        });

        const loadedProducts = await Promise.all(productPromises);
        loadedProducts.forEach((product) => {
          grid.appendChild(product);
        });

        // Re-initialize components for new products
        this.reinitializeNewProducts(loadedProducts);

        window.history.pushState({}, "", url.toString());

        const totalLoadedProducts = this.currentPage * productsPerPage;
        const hasMorePages = totalLoadedProducts < totalProducts;

        if (!hasMorePages) {
          this.hasNextPage = false;
          if (loader) loader.classList.add("hidden");
        } else {
          this.hasNextPage = true;
        }

        if (newProducts.length < productsPerPage) {
          this.hasNextPage = false;
          if (loader) loader.classList.add("hidden");
        }

        console.log('[INFINITY SCROLL] Loaded', newProducts.length, 'new products');
      } catch (error) {
        console.error("[INFINITY SCROLL] Error loading next page:", error);
        this.hasNextPage = false;
        if (loader) loader.classList.add("hidden");
      } finally {
        this.isLoadingNextPage = false;

        if (loader) {
          loader.style.opacity = "0";
          setTimeout(() => loader.classList.add("hidden"), 300);
        }
      }     
    }

    reinitializeNewProducts(products) {
      // Re-initialize add to cart buttons
      products.forEach(product => {
        const addToCartButtons = product.querySelectorAll('.add-to-cart, [data-add-to-cart-text], button[name="add"]');
        addToCartButtons.forEach(button => {
          if (typeof window.updateAddToCartButton === 'function') {
            window.updateAddToCartButton(button);
          }
        });

        // Re-initialize quick view buttons
        const quickViewButtons = product.querySelectorAll('.quick-view-button');
        quickViewButtons.forEach(button => {
          button.setAttribute('data-initialized', 'true');
        });
      });

      // Update all add to cart buttons
      if (typeof window.updateAddToCartButtons === 'function') {
        window.updateAddToCartButtons();
      }

      console.log('[INFINITY SCROLL] Re-initialized', products.length, 'new products');
    }
  }

  // Initialize infinity scroll when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    // Small delay to ensure all other components are initialized
    setTimeout(() => {
      window.InfinityScroll = new InfinityScroll();
    }, 500);
  });

  // Scroll to Top Button Functionality
  class ScrollToTop {
    constructor() {
      this.button = null;
      this.isEnabled = false;
      this.init();
    }

    init() {
      // Check if scroll to top is enabled in theme settings
      this.checkSettings();
      
      if (this.isEnabled) {
        // Create scroll to top button
        this.createButton();
        
        // Add scroll event listener
        window.addEventListener('scroll', () => this.toggleButton());
        
        // Add click event listener
        this.button.addEventListener('click', () => this.scrollToTop());
      }
    }

    checkSettings() {
      // Check if the setting exists and is enabled
      const settingElement = document.querySelector('meta[name="shopify-theme-scroll-to-top"]');
      if (settingElement) {
        this.isEnabled = settingElement.content === 'true';
      } else {
        // Fallback: check if we can access theme settings
        // This will be handled by the Liquid template
        this.isEnabled = true; // Default to enabled
      }
    }

    createButton() {
      this.button = document.createElement('button');
      this.button.className = 'scroll-to-top';
      this.button.setAttribute('aria-label', 'Sayfanın en üstüne git');
      this.button.setAttribute('title', 'Yukarı git');
      document.body.appendChild(this.button);
    }

    toggleButton() {
      if (window.pageYOffset > 300) {
        this.button.classList.add('show');
      } else {
        this.button.classList.remove('show');
      }
    }

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  // Initialize scroll to top when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    new ScrollToTop();
  });

})();
